import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import TwoLineCardSkeleton from "../components/SkeletonCards/TwoLineCardSkeleton";
import useFetch from "../hooks/useFetch";
import { Event } from "../types";
import EntityAddUpdateForm from "../components/EntityAddUpdateForm/EntityAddUpdateForm";
import TextInput from "../components/ui/Input/TextInput";
import SelectInput from "../components/ui/Input/SelectInput";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from '../components/ui/Input/Input.module.css'; // Adjust the relative path
import { format } from 'date-fns';

const Events: React.FC = React.memo(() => {
  //console.log("Rendering Events");
  const navigate = useNavigate();
  const params = useParams<{ competition_id: string }>();
  const competition_id = params?.competition_id;
  const [eventId, setEventId] = useState<number>(NaN);
  const [eventDate, setEventDate] = useState<string | Date>(""); // Default to an empty string

  const [location, setLocation] = useState<string>("");
  const [competition, setCompetition] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isUpdateActive, setIsUpdateActive] = useState(false);

  const { data, setData, errorContent, isLoading, fetchData } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/events`,
    method: "POST",
  });

  const { fetchData: fetchCompetitionData, data: competitionsData } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/competitions/`,
    method: "GET",
  });

  useEffect(() => {
    fetchData({ competitions_id: competition_id });
    fetchCompetitionData();
  }, []);

  if (errorContent) return errorContent;

  /////check from here m 
  /*if (isLoading) {
    return <div>Loading...</div>; // Display a loading indicator
  }*/

  /*if (!hasFetched) {
    return null; // Prevent rendering until fetch is complete
  }*/

  const clearInputFields = () => {
    setLocation("");
    setEventDate("");
    setCompetition("");
  };

  const handleDeleteEvent = async (eventId: number) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this Event?"
    );
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/events/delete/${eventId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        setSuccessMessage("Event deleted successfully!");
        setErrorMessage(null);

        setData((prevData: any) => ({
          ...prevData,
          events: prevData.events.filter(
            (event: Event) => event.id !== eventId
          ),
        }));
      } else {
        let errorMessage = "Error deleting Event: "; let errorMessagedetail;
        if (response.headers.get("content-type")?.includes("application/json")) {
          // Only try to parse JSON if the response is of JSON type
          const result = await response.json();
          if (result.error.error_message?.includes("Cannot delete or update a parent row")) {
            errorMessagedetail = "This event contains child records, please remove them first.";
          } else {
            errorMessagedetail = result.message || errorMessage;
          }
          errorMessage += errorMessagedetail;
        } else {
          console.warn("Response is not JSON:", response);
        }
        setErrorMessage(errorMessage);
        setSuccessMessage(null);
      }
    } catch (err) {
      console.error("Delete Event Error:", err);
      setErrorMessage("Failed to delete event, please try again.");
      setSuccessMessage(null);
    }
  };

  const populateFieldsForUpdate = (event: {
    id: number;
    location: string;
    event_date: string;
    competition: string; // competition ID as a string
  }) => {
    const formatedEventDate = new Date(event.event_date);
    const formatedEventDateString = formatedEventDate
      .toISOString()
      .split("T")[0];
  
    setLocation(event.location);
    setEventDate(formatedEventDateString);
    setCompetition(event.competition);  // Set competition ID

    setIsUpdateActive(true); // Enable edit mode
  };  
  
  const handleSubmitEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    const fields = {
      location: { value: location, message: "Location is empty." },
      eventDate: { value: eventDate, message: "Event date is empty." },
      competition: { value: competition, message: "Competition must be selected." }
    };
    
    for (const { value, message } of Object.values(fields)) {
      if (typeof value === 'string' && !value.trim()) {
        setErrorMessage(message);
        return;
      }
      if (value instanceof Date && isNaN(value.getTime())) {
        // Handle invalid Date
        setErrorMessage(message);
        return;
      }
    }    
    
    const formattedEventDate = format(new Date(eventDate), 'yyyy-MM-dd');

    const eventData = {
      competitions_id: competition,
      event_date: formattedEventDate,
      location,
    };

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/events/add`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(eventData),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSuccessMessage("Event added successfully!");
        setErrorMessage(null);

        setData((prevData: any) => ({
          ...prevData,
          events: [...prevData.events, result.data],
        }));
      } else {
        setErrorMessage(result.errors[0].error || "Error adding event");
        setSuccessMessage(null);
      }
      clearInputFields();
    } catch (err) {
      setErrorMessage("Failed to add event, please try again.");
      setSuccessMessage(null);
    }
  };

  const sendUpdatedEventData = async (eventId: number) => {
    const fields = {
      location: { value: location, message: "Location is empty." },
      eventDate: { value: eventDate, message: "Event date is empty." },
      competition: { value: competition, message: "Competition must be selected." }
    };
    
    for (const { value, message } of Object.values(fields)) {
      if (typeof value === 'string' && !value.trim()) {
        setErrorMessage(message);
        return;
      }
      if (value instanceof Date && isNaN(value.getTime())) {
        // Handle invalid Date
        setErrorMessage(message);
        return;
      }
    }    

    const formattedEventDate = format(new Date(eventDate), 'yyyy-MM-dd');

    const eventData = {
      competitions_id: competition,
      event_date: formattedEventDate,
      location,
    };

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/events/update/${eventId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(eventData),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSuccessMessage("Event updated successfully!");
        setErrorMessage(null);

        // Update the local users state
        setData((prevData: any) => ({
          ...prevData,
          events: prevData.events.map((event: Event) =>
            event.id === result.data.id ? { ...event, ...result.data } : event
          ),
        }));
      } else {
        setErrorMessage(result.errors[0].error || "Error updating event");
        setSuccessMessage(null);
      }
    } catch (err) {
      setErrorMessage("Failed to update event, please try again.");
      setSuccessMessage(null);
    }
  };

  const handleUpdateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendUpdatedEventData(eventId);
    clearInputFields();
    setIsUpdateActive(false);
  };

  /*const formatDateToYMD = (date: Date | null): string => {
    if (!date) return ""; // If no date is selected, return an empty string
  
    // Ensure that the date is a valid Date object
    const validDate = new Date(date);
    if (isNaN(validDate.getTime())) return ""; // If the date is invalid, return an empty string
  
    const year = validDate.getFullYear();
    const month = (validDate.getMonth() + 1).toString().padStart(2, "0");
    const day = validDate.getDate().toString().padStart(2, "0");
  
    return `${year}-${month}-${day}`;
  };*/
    
  const currentRole = JSON.parse(localStorage.getItem("user") || '{}').current_role;

  //const canSeePerformancesButton_dummmy = Boolean( JSON.parse(localStorage.getItem("user") || '{}')?.modules?.[3]?.ModulesPermissions?.view );
  //alternatively
  //const canSeePerformancesButton_dummmy = !!JSON.parse(localStorage.getItem("user") || '{}')?.modules?.find(m => m.title === "Performances")?.ModulesPermissions?.view;
  //console.log("canSeePerformancesButton_dummmy ----", canSeePerformancesButton_dummmy);

//  const canSeePerformances = canSeePerformancesButton_dummmy;
/*
  const canSeePerformances = [1, 7, 8].includes(Number(currentRole));
  const canSeeDivisions    = [1, 3, 7, 8].includes(Number(currentRole));
  const canSeeMusicBtn     = [1, 3].includes(Number(currentRole));
  const isAdmin            = Number(currentRole) === 1;
  const isBlockedFromBoxClick = ![1, 8,7, 3].includes(currentRole);
*/
  //const canSeePerformances = !!JSON.parse(localStorage.getItem("user") || '{}')?.modules?.find((m:any) => m.title.toLowerCase() === "performances")?.ModulesPermissions?.view;
  const canSeeAdminPerformances = !!JSON.parse(localStorage.getItem("user") || '{}')?.modules?.find((m:any) => m.title.toLowerCase() === "adminperformances")?.ModulesPermissions?.view;
  const canSeeDivisions    = !!JSON.parse(localStorage.getItem("user") || '{}')?.modules?.find((m:any) => m.title.toLowerCase() === "divisions")?.ModulesPermissions?.view;
  const canSeeMusicBtn     = !!JSON.parse(localStorage.getItem("user") || '{}')?.modules?.find((m:any) => m.title.toLowerCase() === "eventsongs")?.ModulesPermissions?.view;
  
  const isAdmin = Number(currentRole) === 1;

  const isBlockedFromBoxClick = (![1,8,7,3,4].includes(currentRole) && canSeeDivisions);

      return (
        <section>
          {isAdmin ? (
            <div className="form-container">
              <h2 style={{ fontSize: "24px" }}>Add/Edit Event</h2>
              <EntityAddUpdateForm
                handleUserSubmit={
                  isUpdateActive ? handleUpdateEvent : handleSubmitEvent
                }
              >
                <TextInput
                  placeholder="Location"
                  value={location}
                  onChange={(e) => {
                    setErrorMessage(null);
                    setSuccessMessage(null);
                    setLocation(e.target.value);
                  }}
                />
                            
                <DatePicker
                  id="eventDate"
                  selected={eventDate ? new Date(eventDate) : null}
                  onChange={(date: Date | null) => {
                    if (date) {
                      setEventDate(date);
                    }
                  }}
                  dateFormat="MM/dd/yyyy"
                  autoComplete="off"
                  placeholderText="Event Date"
                  isClearable={false}
                  customInput={<input className={styles.singleInput} style={{ width: "auto" }} />}
                />

              
                <SelectInput
                  value={competition || ""}  // Ensure the value is set correctly, using empty string as fallback
                  options={competitionsData.map((comp: any) => ({
                    value: comp.id,
                    label: (comp.title + " - " +  comp.start_date ),
                  }))}
                  name="competitions"
                  onChange={(e) => {
                    setErrorMessage(null);
                    setSuccessMessage(null);
                    setCompetition(e.target.value);
                  }}
                />
              </EntityAddUpdateForm>
              {successMessage && <p style={{ color: "green" }}>{successMessage}</p>}
              {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
            </div>
          ) : null}
          <div className="container">
            <div className="div-header-title-section">
              <h1 className="judgingapp-heading2">{data?.competition?.title}</h1>
              <div
                className="link-block w-inline-block cursor-pointer"
                onClick={() => {
                  /*if (currentRole == "1" || currentRole == "8" || currentRole == "7") {
                    navigate("/admin/competitions");
                  } else {
                    navigate("/competitions");
                  }*/
                  navigate("/competitions");
                }}
              >
                <img
                  src="/images/back-icon.png"
                  loading="lazy"
                  alt=""
                  className="image-2"
                />
                <h1 className="back-text-link">Home</h1>
              </div>
              <p className="paragraph-2">
                {data?.competition?.start_date} - {data?.competition?.end_date}
              </p>
            </div>
            <div className="events__mainBox">
              {isLoading ? (
                <TwoLineCardSkeleton
                  cardsCount={1}
                  baseColor="#d7daef"
                  highlightColor="#e4e4e4"
                />
              ) : (
                data?.events?.map((event: Event) => {
                  return (
                    <div
                      key={event.id}
                      className="small-card cursor-pointer scale--md"
                      onClick={() => {
                        if (isBlockedFromBoxClick) {
                          navigate(`/divisions/${event.id}`);
                        }
                      }}
                    >
                      <h3 className="small-card__primaryHeading">
                        {event.event_date}
                      </h3>
                      <p className="small-card__secondaryHeading">
                        {event.location}
                      </p>
                      {!isBlockedFromBoxClick &&
                        (canSeeAdminPerformances || canSeeDivisions || canSeeMusicBtn) && (
                        <div className="small-card-btnsBox">

                          {canSeeAdminPerformances && (
                            <button
                              className="action-btn"
                              onClick={() => navigate(`/admin/performances/${event.id}`)}
                            >
                              Performances
                            </button>
                          )}

                          {canSeeDivisions && (
                            <button
                              className="action-btn"
                              onClick={() => navigate(`/divisions/${event.id}`)}
                            >
                              Divisions
                            </button>
                          )}

                          {canSeeMusicBtn && (
                            <button
                              className="action-btn"
                              onClick={() => navigate(`/music/${event.id}`)}
                            >
                              Music
                            </button>
                          )}

                          {isAdmin && (
                            <>
                              <button
                                className="action-btn"
                                onClick={() => {
                                  setEventId(event.id);
                                  populateFieldsForUpdate({
                                    id: event.id,
                                    event_date: event.event_date,
                                    location: event.location,
                                    competition: String(data?.competition.id),
                                  });
                                }}
                              >
                                Edit
                              </button>

                              <button
                                className="action-btn"
                                onClick={() => handleDeleteEvent(event.id)}
                              >
                                Delete
                              </button>
                            </>
                          )}

                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </section>
      );
});


export default Events;
